// redux/slices/userProjectsSlice.ts
import { createSlice } from '@reduxjs/toolkit';
import { UserProjectState } from '../types/userProjectTypes';
import { fetchUserProjects, refreshUserProjects, fetchUserProjectsDashboardData, fetchUserProjectsByStatus } from '../actions/userProjectsAction';

// Initial state
const initialState: UserProjectState = {
  userProjects: [],
  pagination: null,
  loading: false,
  error: null,
  lastLoadTime: 0,
  // Initialize filtered user projects
  filteredUserProjects: {},
  // Initialize dashboard data
  userProjectCount: 0,
  userPaymentReceived: 0,
  userBilledAmount: 0,
  userPipelineAmount: 0,
  userStatusCounts: {
    UNASSIGNED: 0,
    ASSIGNED: 0,
    IN_PROGRESS: 0,
    REVIEW: 0,
    DELIVERED: 0,
    BILLED: 0,
    PAYMENT_RECEIVED: 0,
    REOPEN: 0,
    HOLD: 0,
    CANCELLED: 0,
    NON_ACTIVE: 0
  },
  // Dashboard API response
  dashboardData: null,
  dashboardDataLoading: false,
  dashboardDataError: null,
  dashboardDataTimestamp: 0
};

console.log('[Slice] userProjectsSlice - Initializing with state:', initialState);

const userProjectsSlice = createSlice({
  name: 'userProjects',
  initialState,
  reducers: {
    clearUserProjectsCache: (state) => {
      console.log('[Slice] userProjectsSlice - Clearing cache');
      state.lastLoadTime = 0;
      state.filteredUserProjects = {}; // Clear filtered projects cache too
      state.userProjects = []; // Clear main user projects data
      state.pagination = null; // Clear pagination info
      state.error = null; // Clear any existing errors
    },
    clearSpecificStatusCache: (state, action) => {
      console.log('[Slice] userProjectsSlice - Clearing cache for status:', action.payload);
      const status = action.payload;
      if (status === 'All') {
        // Clear main user projects data
        state.userProjects = [];
        state.pagination = null;
        state.lastLoadTime = 0;
      } else {
        // Clear specific status data
        delete state.filteredUserProjects[status];
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchUserProjects action
      .addCase(fetchUserProjects.pending, (state) => {
        console.log('[Slice] userProjectsSlice - fetchUserProjects pending');
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProjects.fulfilled, (state, action) => {
        console.log('[Slice] userProjectsSlice - fetchUserProjects fulfilled');
        console.log('[Slice] userProjectsSlice - received data count:', action.payload.data?.length || 0);
        
        state.loading = false;
        
        // Make sure we have an array of projects
        if (Array.isArray(action.payload.data)) {
          state.userProjects = action.payload.data;
          state.pagination = action.payload.pagination || null;
          state.lastLoadTime = action.payload.lastLoadTime || Date.now();
          
          // Update dashboard data if available
          if (action.payload.dashboardData) {
            state.userProjectCount = action.payload.dashboardData.userProjectCount || 0;
            state.userPaymentReceived = action.payload.dashboardData.userPaymentReceived || 0;
            state.userBilledAmount = action.payload.dashboardData.userBilledAmount || 0;
            state.userPipelineAmount = action.payload.dashboardData.userPipelineAmount || 0;
            state.userStatusCounts = action.payload.dashboardData.userStatusCounts || initialState.userStatusCounts;
            
            console.log('[Slice] userProjectsSlice - Updated dashboard data:', { 
              userProjectCount: state.userProjectCount,
              userPaymentReceived: state.userPaymentReceived,
              userBilledAmount: state.userBilledAmount,
              userPipelineAmount: state.userPipelineAmount
            });
          }
          
          console.log('[Slice] userProjectsSlice - Updated state:', { 
            projectCount: state.userProjects.length,
            pagination: state.pagination,
            lastLoadTime: new Date(state.lastLoadTime).toISOString()
          });
        } else {
          console.error('[Slice] userProjectsSlice - Invalid data format:', action.payload.data);
        }
      })
      .addCase(fetchUserProjects.rejected, (state, action) => {
        console.log('[Slice] userProjectsSlice - fetchUserProjects rejected:', action.payload);
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle fetchUserProjectsByStatus action
      .addCase(fetchUserProjectsByStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProjectsByStatus.fulfilled, (state, action) => {
        state.loading = false;

        // Store the filtered user projects by status
        if (action.payload && action.payload.status) {
          const status = action.payload.status;
          state.filteredUserProjects = {
            ...state.filteredUserProjects,
            [status]: action.payload.data,
          };

          // Do NOT overwrite the main userProjects state with filtered data
          // This ensures "All" status can still access the complete dataset
        }
      })
      .addCase(fetchUserProjectsByStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle refreshUserProjects action
      .addCase(refreshUserProjects.pending, (state) => {
        console.log('[Slice] userProjectsSlice - refreshUserProjects pending');
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshUserProjects.fulfilled, (state, action) => {
        console.log('[Slice] userProjectsSlice - refreshUserProjects fulfilled');
        console.log('[Slice] userProjectsSlice - received data count:', action.payload.data?.length || 0);
        
        state.loading = false;
        
        // Make sure we have an array of projects
        if (Array.isArray(action.payload.data)) {
          state.userProjects = action.payload.data;
          state.pagination = action.payload.pagination || null;
          state.lastLoadTime = action.payload.lastLoadTime || Date.now();
          
          // Update dashboard data if available
          if (action.payload.dashboardData) {
            state.userProjectCount = action.payload.dashboardData.userProjectCount || 0;
            state.userPaymentReceived = action.payload.dashboardData.userPaymentReceived || 0;
            state.userBilledAmount = action.payload.dashboardData.userBilledAmount || 0;
            state.userPipelineAmount = action.payload.dashboardData.userPipelineAmount || 0;
            state.userStatusCounts = action.payload.dashboardData.userStatusCounts || initialState.userStatusCounts;
            
            console.log('[Slice] userProjectsSlice - Updated dashboard data:', { 
              userProjectCount: state.userProjectCount,
              userPaymentReceived: state.userPaymentReceived,
              userBilledAmount: state.userBilledAmount,
              userPipelineAmount: state.userPipelineAmount
            });
          }
          
          console.log('[Slice] userProjectsSlice - Updated state after refresh:', { 
            projectCount: state.userProjects.length,
            pagination: state.pagination,
            lastLoadTime: new Date(state.lastLoadTime).toISOString()
          });
        } else {
          console.error('[Slice] userProjectsSlice - Invalid data format:', action.payload.data);
        }
      })
      .addCase(refreshUserProjects.rejected, (state, action) => {
        console.log('[Slice] userProjectsSlice - refreshUserProjects rejected:', action.payload);
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle fetchUserProjectsDashboardData action
      .addCase(fetchUserProjectsDashboardData.pending, (state) => {
        console.log('[Slice] userProjectsSlice - fetchUserProjectsDashboardData pending');
        state.dashboardDataLoading = true;
        state.dashboardDataError = null;
      })
      .addCase(fetchUserProjectsDashboardData.fulfilled, (state, action) => {
        console.log('[Slice] userProjectsSlice - fetchUserProjectsDashboardData fulfilled');
        state.dashboardDataLoading = false;
        state.dashboardData = action.payload.data;
        state.dashboardDataTimestamp = action.payload.timestamp || Date.now();
        
        // Update status counts in local state if available in the API response
        if (action.payload.data.statusCounts) {
          state.userStatusCounts = action.payload.data.statusCounts;
        }
        
        console.log('[Slice] userProjectsSlice - Updated dashboard data:', state.dashboardData);
      })
      .addCase(fetchUserProjectsDashboardData.rejected, (state, action) => {
        console.log('[Slice] userProjectsSlice - fetchUserProjectsDashboardData rejected:', action.payload);
        state.dashboardDataLoading = false;
        state.dashboardDataError = action.payload as string;
      });
  }
});

export const { clearUserProjectsCache, clearSpecificStatusCache } = userProjectsSlice.actions;
export default userProjectsSlice.reducer;
